import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useUserContext } from "../../../context/UserProvider";
import { db } from "../../../firebase";
import { HeartIcon, MenuDotsIcon, PaperPlaneIcon, TextBubbleIcon } from "../../util/Icons";
import ProfilePicture from "../../util/ProfilePicture";
import SingleComment from "./SingleComment";
import { collection, doc, getDoc, getDocs, limit, orderBy, query, runTransaction, startAfter, updateDoc } from "firebase/firestore";
import { ProgressComponent, WhoLiked } from "./SocialFeed";
import DisplayName from "../../util/DisplayName";

const POST_URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
const DEFAULT_HASHTAG_BADGE_STYLES = {
    text: "text-indigo-600",
    lightBg: "bg-indigo-50",
    lightBorder: "border-indigo-200",
};

function getHashtagBadgeStyles(hashtag, hashtagOptions) {
    return hashtagOptions.find((option) => option.name === hashtag)?.styles || DEFAULT_HASHTAG_BADGE_STYLES;
}

function trimTrailingUrlPunctuation(url) {
    const match = url.match(/[.,!?;:)]*$/);
    if (!match || !match[0]) {
        return { href: url, trailing: "" };
    }

    const trailing = match[0];
    return {
        href: url.slice(0, -trailing.length),
        trailing,
    };
}

function renderPostMessage(message) {
    if (typeof message !== "string") return null;

    return message.split(POST_URL_REGEX).map((part, index) => {
        if (!part.match(POST_URL_REGEX)) return part;
        const { href, trailing } = trimTrailingUrlPunctuation(part);
        const linkHref = href.startsWith("www.") ? `https://${href}` : href;
        return (
            <span key={`${href}-${index}`}>
                <a href={linkHref} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-words">
                    {href}
                </a>
                {trailing}
            </span>
        );
    });
}

/**
 *
 * @param {{post: {id: String}, deletePost: () => {}, editPost: () => {}}} post Post data
 * @param {Function} deletePost Function that deletes posts
 * @param {Function} editPost Function that edits posts
 * @returns
 */
export default function SinglePost({ post, deletePost, editPost, hashtagOptions = [] }) {
    const { user, userDetails, userData } = useUserContext();
    const { postId } = useParams();
    const [hasLikedPost, setHasLikedPost] = useState(false);
    const [likeCount, setLikeCount] = useState(post?.count_likes || 0);
    const [enableLikeButton, setEnableLikeButton] = useState(false);
    const [enableCommentButton, setEnableCommentButton] = useState(true);
    const [commentsCount, setCommentsCount] = useState(post?.count_comments || 0);
    const commentRef = useRef();
    const [showComments, setShowComments] = useState(true);
    const [commentsMap, setCommentsMap] = useState([]);
    const [lastComment, setLastComment] = useState(null);
    const [showMenu, setShowMenu] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [isEditingPost, setIsEditingPost] = useState(false);
    const [editMessage, setEditMessage] = useState(post?.message || "");
    const [editError, setEditError] = useState("");
    const [isSavingPostEdit, setIsSavingPostEdit] = useState(false);
    const likesCollectionRef = useMemo(() => {
        if (!post?.id) return null
        return collection(db, "social_feed", post.id, "likes")
    }, [post])
    useEffect(() => {
        getPostLikes();
        getComments();
    }, []);
    useEffect(() => {
        if (!isEditingPost) {
            setEditMessage(post?.message || "");
        }
    }, [post?.message, isEditingPost]);
    async function getPostLikes() {
        let hasLiked = await getDoc(doc(db, "social_feed", post?.id, "likes", user.uid));
        setHasLikedPost(hasLiked.exists());
        setEnableLikeButton(true);
    }
    async function getComments() {
        console.log("Getting comments");
        const q = query(collection(db, "social_feed", post?.id, "comments"), orderBy("epochTime", "desc"), limit(3));
        const commentsSnap = await getDocs(q);
        let newCommentsMap = [];
        commentsSnap.forEach((comment) => {
            let commentData = comment.data();
            commentData.id = comment.id;
            newCommentsMap.push(commentData);
        });
        setCommentsMap([...newCommentsMap]);
        setLastComment(commentsSnap.docs[commentsSnap.docs.length - 1]);
    }
    async function getMoreComments() {
        if (lastComment == null) return;
        console.log("Getting MORE comments");
        const q = query(collection(db, "social_feed", post?.id, "comments"), orderBy("epochTime", "desc"), startAfter(lastComment), limit(3));
        const commentsSnap = await getDocs(q);
        let newCommentsMap = [];
        commentsSnap.forEach((comment) => {
            let commentData = comment.data();
            commentData.id = comment.id;
            newCommentsMap.push(commentData);
        });
        setCommentsMap([...commentsMap, ...newCommentsMap]);
        setLastComment(commentsSnap.docs[commentsSnap.docs.length - 1]);
    }
    async function setLikePost() {
        if (!enableLikeButton) return;
        setEnableLikeButton(false);
        const likeRef = doc(db, "social_feed", post?.id, "likes", user.uid);
        const postRef = doc(db, "social_feed", post?.id);
        if (hasLikedPost) {
            // Dislike
            runTransaction(db, async (transaction) => {
                const postSnap = await transaction.get(postRef);
                if (!postSnap.exists()) {
                    throw new Error("Post doesn't exist!");
                }
                const likeSnap = await transaction.get(likeRef);
                if (!likeSnap.exists()) {
                    throw new Error("Like doc doesn't exist!");
                }
                // Remove like doc
                transaction.delete(likeRef);
                // Update post likes
                const newLikes = (postSnap.data().count_likes || 0) - 1;
                transaction.update(postRef, { count_likes: newLikes });
            })
                .then(() => {
                    setHasLikedPost(false);
                    setLikeCount(likeCount - 1);
                })
                .catch((err) => {
                    console.warn("Could not dislike", err.message);
                });
        } else {
            // Like
            runTransaction(db, async (transaction) => {
                const postSnap = await transaction.get(postRef);
                if (!postSnap.exists()) {
                    throw new Error("Post doesn't exist!");
                }
                // Add to like collection
                transaction.set(likeRef, {
                    uid: user.uid,
                    authorId: user.uid,
                    epochTime: Date.now(),
                    userName: userDetails?.firstName + " " + userDetails?.lastName,
                    postAuthorId: post?.authorID,
                    postId: post?.id,
                });
                // Update post likes
                const newLikes = (postSnap.data().count_likes || 0) + 1;
                transaction.update(postRef, { count_likes: newLikes });
            })
                .then(() => {
                    setHasLikedPost(true);
                    setLikeCount(likeCount + 1);
                })
                .catch((err) => {
                    console.error(err.message);
                });
        }
        // Added delay
        await new Promise((resolve) => setTimeout(resolve, 2000));
        setEnableLikeButton(true);
    }
    async function addComment() {
        console.log("Comment start");
        if (!enableCommentButton) return;
        if (!commentRef?.current) return;
        const message = commentRef?.current?.value;
        if (message.length < 1) {
            console.warn("Comment must be at least 1 chars");
            return;
        }
        setEnableCommentButton(false);
        const newCommentRef = doc(collection(db, "social_feed", post?.id, "comments"));
        const postRef = doc(db, "social_feed", post?.id);
        const commentData = {
            postAuthorId: post?.authorID,
            postId: post?.id,
            commentAuthorID: user.uid,
            authorId: user.uid,
            authorName: userDetails.firstName + " " + userDetails.lastName,
            message: message,
            createdAt: new Date().toISOString(),
            epochTime: Date.now(),
            count_likes: 0,
            replyTo: replyTo,
            id: newCommentRef.id,
        };
        runTransaction(db, async (transaction) => {
            const postSnap = await transaction.get(postRef);
            if (!postSnap.exists()) throw new Error("Post doesn't exist!");
            // Add to comments collection
            transaction.set(newCommentRef, commentData);
            // Update posts comment count
            const newCommentCount = (postSnap.data().count_comments || 0) + 1;
            transaction.update(postRef, { count_comments: newCommentCount });
        })
            .then(() => {
                setCommentsCount((prev) => prev + 1);
                commentData.id = newCommentRef.id;
                setCommentsMap([commentData, ...commentsMap]);
                commentRef.current.value = "";
                commentRef.current.style.height = 'auto';
            })
            .catch((err) => {
                console.error(err.message);
            });

        setReplyTo(null);
        await new Promise((resolve) => setTimeout(resolve, 2000));
        setEnableCommentButton(true);
    }
    async function editComment(comment, message) {
        const commentId = comment?.id;
        const commentPostId = comment?.postId || comment?.ogPostID || post?.id;
        const commentAuthorId = comment?.commentAuthorID || comment?.authorId;
        if (!commentId || !commentPostId) return;
        if (user.uid !== commentAuthorId) return;

        const editTime = Date.now();
        const commentPatch = {
            message,
            editedAt: editTime,
            updatedAt: new Date(editTime).toISOString(),
        };

        await updateDoc(doc(db, "social_feed", commentPostId, "comments", commentId), commentPatch);
        setCommentsMap((prev) => {
            return prev.map((item) => {
                if (item.id !== commentId) return item;
                return { ...item, ...commentPatch };
            });
        });

        return commentPatch;
    }
    function startEditingPost() {
        setEditMessage(post?.message || "");
        setEditError("");
        setIsEditingPost(true);
    }
    function cancelEditingPost() {
        setEditMessage(post?.message || "");
        setEditError("");
        setIsEditingPost(false);
    }
    async function savePostEdit() {
        if (isSavingPostEdit || typeof editPost !== "function") return;
        const nextMessage = editMessage.trim();
        if (nextMessage.length < 1) {
            setEditError("Message must be at least 1 character");
            return;
        }
        if (nextMessage === (post?.message || "").trim()) {
            setIsEditingPost(false);
            return;
        }
        setIsSavingPostEdit(true);
        setEditError("");
        try {
            await editPost(post, nextMessage);
            setIsEditingPost(false);
        } catch (err) {
            console.error("Could not edit post:", err.message);
            setEditError("Failed to save post. Please try again.");
        }
        setIsSavingPostEdit(false);
    }

    return (
        <div className="w-auto relative flex flex-col border border-slate-200 rounded-xl pt-3 px-3 pb-1 my-2 box-border bg-white shadow-md overflow-y-auto overflow-hidden max-h-full">
            <div className="flex mb-3 gap-2 items-center">
                <Link className="no-underline" to={`/profile/${post?.authorID}`}>
                    <ProfilePicture name={post?.authorName} userID={post?.authorID}></ProfilePicture>
                </Link>
                <div>
                    <Link className="no-underline text-current" to={`/profile/${post?.authorID}`}>
                        {/* <p className="font-medium mb-0 text-md">{post?.authorName}</p> */}
                        <DisplayName uid={post?.authorID} fullName={post?.authorName} />
                    </Link>
                    <p className="font-light text-xs text-slate-500 mb-0">
                        {new Date(post?.epochTime).toLocaleString("en-US", {
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true,
                        })}
                        {(post?.editedAt || post?.updatedAt) && <span className="ml-1 text-slate-400">Edited</span>}
                    </p>
                    {post?.hashtags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                            {post.hashtags.map((hash) => {
                                const styles = getHashtagBadgeStyles(hash, hashtagOptions);
                                return (
                                    <span
                                        key={post.id + hash}
                                        className={`${styles.text} ${styles.lightBg} ${styles.lightBorder} border rounded-md px-1.5 py-0.5 text-[11px] font-medium cursor-pointer transition-[filter] hover:brightness-95`}
                                    >
                                        #{hash}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="flex justify-between items-start px-1 self-start absolute right-2">
                    {/* {!postId && <Link to={`/p/${post?.id}`}>full</Link>} */}
                    {user.uid === post?.authorID && (typeof deletePost === "function" || typeof editPost === "function") && (
                        <div className="relative ml-auto">
                            <button className="p-1 rounded-full hover:bg-slate-100 transition-colors text-slate-500" onClick={() => setShowMenu(!showMenu)}>
                                <MenuDotsIcon size={24} />
                            </button>
                            {showMenu && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)}></div>
                                    <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-slate-100 py-1 z-20 overflow-hidden">
                                        {typeof editPost === "function" && (
                                            <button
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors font-medium flex items-center gap-2"
                                                onClick={() => {
                                                    startEditingPost();
                                                    setShowMenu(false);
                                                }}
                                            >
                                                <span>Edit</span>
                                            </button>
                                        )}
                                        {typeof deletePost === "function" && (
                                            <button
                                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors font-medium flex items-center gap-2"
                                                onClick={() => {
                                                    deletePost(post);
                                                    setShowMenu(false);
                                                }}
                                            >
                                                <span>Delete</span>
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {isEditingPost ? (
                <div className="mb-3">
                    <textarea
                        value={editMessage}
                        onChange={(e) => {
                            setEditMessage(e.target.value);
                            setEditError("");
                        }}
                        rows={4}
                        className="border border-slate-300 rounded-lg px-3 py-2 w-full bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm resize-y"
                        disabled={isSavingPostEdit}
                    />
                    {editError && <p className="text-red-500 text-sm mt-1 mb-0">{editError}</p>}
                    <div className="flex justify-end gap-2 mt-2">
                        <button
                            className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition-colors disabled:opacity-50"
                            onClick={cancelEditingPost}
                            disabled={isSavingPostEdit}
                        >
                            Cancel
                        </button>
                        <button
                            className="px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={savePostEdit}
                            disabled={isSavingPostEdit}
                        >
                            {isSavingPostEdit ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            ) : (
                <p className="mb-2 whitespace-pre-line">{renderPostMessage(post?.message)}</p>
            )}
            {post?.shareRace && (
                <ProgressComponent
                    imgUrl={post?.shareRace?.raceInfo.imageUrl}
                    raceName={post?.shareRace?.raceInfo.name}
                    legName={post?.shareRace?.raceInfo.legName}
                    progress={post?.shareRace?.progress}
                    teamProgress={post?.shareRace?.teamProgress || null}
                    distance={post?.shareRace?.raceInfo.distance}
                ></ProgressComponent>
            )}
            {post?.pictures && (
                <div className="-mx-3 mb-2">
                    <PictureCarousel pictures={post.pictures} />
                </div>
            )}

            <div className="flex gap-3 text-slate-500 select-none">
                <button
                    className={`like-button py-1 flex gap-2 cursor-pointer hover:!text-red-500 transition-transform enabled:active:scale-90`}
                    style={{ color: hasLikedPost ? "#ef4444" : "currentcolor", cursor: "pointer" }}
                    disabled={!enableLikeButton}
                    onClick={(e) => {
                        let parent = e.target.closest("button.like-button");
                        if (!hasLikedPost) {
                            parent.classList.add("heart-animate");
                        }
                        setLikePost();
                    }}
                    onAnimationEnd={(e) => e.target.classList.remove("heart-animate")}
                >
                    <HeartIcon size={22} style={hasLikedPost ? "fill" : "stroke"} color={"currentcolor"} />
                    <span>{likeCount}</span>
                </button>
                <div
                    className="py-1 flex gap-2 cursor-pointer hover:text-green-500 transition-transform active:scale-90"
                    onClick={() => {
                        if (!showComments) {
                            setShowComments(true);
                        }
                        // Small delay to ensure the textarea is rendered before focusing
                        setTimeout(() => {
                            commentRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                            commentRef?.current?.focus();
                        }, 100);
                    }}
                >
                    <TextBubbleIcon size={22} style={"stroke"} color={"currentcolor"} />
                    <span>{commentsCount}</span>
                </div>
            </div>
            <WhoLiked count={likeCount} likeCollection={likesCollectionRef} />
            <hr />
            {showComments && (
                <div>
                    {commentsMap.map((comment) => {
                        return (
                            <SingleComment
                                key={comment?.id}
                                commentData={comment}
                                editComment={editComment}
                                onClick={() => {
                                    setReplyTo(comment);
                                    commentRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                                    commentRef?.current?.focus();
                                }}
                            ></SingleComment>
                        );
                    })}
                    {commentsMap.length < commentsCount && (
                        <button
                            onClick={() => {
                                getMoreComments();
                            }}
                            className="block mx-auto w-fit py-1.5 px-3 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
                        >
                            Load more
                        </button>
                    )}
                    {replyTo && (
                        <div className="relative flex justify-between items-center bg-slate-100 rounded-lg px-3 py-2 mb-2">
                            <span className="text-xs text-slate-600">
                                <span className="font-bold">Replying to:</span> {replyTo?.authorName}
                            </span>
                            <button
                                type="button"
                                className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 text-slate-500 hover:bg-slate-300 transition-colors"
                                onClick={() => {
                                    setReplyTo(null);
                                }}
                            >
                                <span className="text-[10px] font-bold leading-none">✕</span>
                            </button>
                        </div>
                    )}
                    <div className="relative w-full mt-2 mb-3">
                        <textarea
                            ref={commentRef}
                            rows={1}
                            className="border border-slate-300 rounded-2xl px-4 py-2 w-full pr-12 bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm resize-none overflow-hidden"
                            placeholder="Comment..."
                            onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    addComment();
                                }
                            }}
                        />
                        <button
                            className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-colors ${enableCommentButton ? "text-blue-500 hover:bg-blue-50 cursor-pointer" : "text-slate-300 cursor-not-allowed"
                                }`}
                            onClick={() => addComment()}
                            disabled={!enableCommentButton}
                        >
                            <PaperPlaneIcon color={"currentColor"} size={20} style={"stroke"}></PaperPlaneIcon>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function PictureCarousel({ pictures }) {
    const slider = useRef();
    let [pos, setPos] = useState(0);
    let slideEnabled = useRef(true);
    function slide(dir = "r") {
        if (!slider.current) return;
        if (!slideEnabled.current) return;
        slideEnabled.current = false;
        let nextPos = pos;
        dir === "r" ? (nextPos += 100) : (nextPos -= 100);
        if (nextPos >= pictures.length * 100) nextPos = 0;
        if (nextPos <= -100) nextPos = (pictures.length - 1) * 100;
        setPos(nextPos);
        setTimeout(() => {
            slideEnabled.current = true;
        }, 100);
    }
    // console.log(pos);
    useEffect(() => {
        // Mobile touch scroll
        if (!slider.current) return;
        if (pictures.length <= 1) return;
        let [start, change, sliderWidth] = [0, 0, 0];
        slider.current.ontouchstart = (e) => {
            start = e.touches[0].clientX;
            sliderWidth = slider.current.clientWidth;
        };
        slider.current.ontouchmove = (e) => {
            change = start - e.touches[0].clientX;
        };
        slider.current.ontouchend = (e) => {
            if (change > sliderWidth / 4) {
                slide("r");
            }
            if (change * -1 > sliderWidth / 4) {
                slide("l");
            }
            [start, change] = [0, 0];
        };
        // return () => {
        //   slider.current.ontouchstart = null;
        //   slider.current.ontouchmove = null;
        //   slider.current.ontouchend = null;
        // };
    }, [slider, pos]);
    if (slider.current) slider.current.style.transform = `translate(-${pos}%,0)`;
    // slider.current.style.translate = `-${pos}%`;
    return (
        <div className="relative overflow-x-hidden w-full h-full">
            <div ref={slider} className="w-full flex gap-0  snap-x snap-mandatory transition-transform ease-out z-0">
                {pictures.map((p) => {
                    return (
                        <div key={p} className="w-full h-96 flex-shrink-0 flex-grow-0 snap-center content-center">
                            <img className="select-none max-w-full max-h-full mx-auto " src={p} alt="" />
                        </div>
                    );
                })}
            </div>
            {pictures?.length > 1 && (
                <>
                    <div className="absolute w-full flex justify-center gap-1.5 bottom-2 z-10 pointer-events-none">
                        {pictures.map((p, index) => {
                            return (
                                <span
                                    key={`i_${p}`}
                                    className="inline-block rounded-full w-1.5 h-1.5 transition-colors shadow-sm"
                                    style={{
                                        backgroundColor: pos / 100 === index ? "white" : "rgba(255, 255, 255, 0.5)",
                                        boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                                    }}
                                    onClick={() => {
                                        setPos(index * 100);
                                    }}
                                ></span>
                            );
                        })}
                    </div>
                    <div className="absolute inset-0 flex justify-between items-center pointer-events-none px-2">
                        <CircleArrow
                            direction={"l"}
                            onClick={() => {
                                slide("l");
                            }}
                        />
                        <CircleArrow
                            direction={"r"}
                            onClick={() => {
                                slide("r");
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

function CircleArrow({ direction = "r" | "l", onClick }) {
    const isRight = direction === "r";
    return (
        <div
            onClick={onClick}
            className={`
        w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-md 
        flex items-center justify-center cursor-pointer pointer-events-auto 
        text-slate-700 hover:text-slate-900 hover:bg-white transition-all
        active:scale-95 z-20
      `}
        >
            <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={!isRight ? "rotate-180" : ""}
            >
                <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
        </div>
    );
}
